import React, { useMemo, type PropsWithChildren } from 'react';
import { styled } from '@compiled/react';
import { useThemeObserver } from '@atlaskit/tokens';
import { getAdminBaseUrl } from '@atlassian/jira-external-urls/src/utils/get-admin-base-url/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useCmdbUpsellBannerStoreActions } from '../../controllers';
import SectionMessage, { SectionMessageAction } from './styled-section/main.tsx';
import type { SectionMessageProps } from './styled-section/types.tsx';

export type Props = {
	title: string;
	primaryAction: string;
	secondaryAction: string;
	bannerArea?: 'services' | 'features';
};

// Usage instructions: Pass title, and text for two button actions. Render description as a child React node.
export const UpsellBanner = (props: PropsWithChildren<Props>) => {
	const { title, primaryAction, secondaryAction, bannerArea = 'services', children } = props;
	const { cloudId, isSiteAdmin } = useTenantContext();
	const { dismissBanner } = useCmdbUpsellBannerStoreActions();

	const tryNowURL = `${getAdminBaseUrl()}/s/${cloudId}/billing/applications/change-edition/jira-servicedesk.ondemand?utm_campaign=P%3Ajira%7CO%3Ajsm%7CI%3Acmdb-upsell-banner-${bannerArea}&utm_source=product&utm_medium=product&utm_content=version_1`;
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';

	const actions = useMemo(() => {
		const actionsCandidates: SectionMessageProps['actions'] = [];

		// Only site admins should be shown the link to a free trial
		if (isSiteAdmin) {
			actionsCandidates.push(
				<SectionMessageAction
					key="1"
					isPrimaryAction
					href={tryNowURL}
					target="_blank"
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'cmdbUpsellTryNow');
					}}
				>
					{primaryAction}
				</SectionMessageAction>,
			);
		}

		actionsCandidates.push(
			<SectionMessageAction
				key="2"
				isPrimaryAction={false}
				onClick={(_, analyticsEvent) => {
					fireUIAnalytics(analyticsEvent, 'cmdbUpsellMaybeLater');
					dismissBanner();
				}}
			>
				{secondaryAction}
			</SectionMessageAction>,
		);

		return actionsCandidates;
	}, [isSiteAdmin, secondaryAction, tryNowURL, primaryAction, dismissBanner]);

	return (
		<BannerWrapper>
			<SectionMessage isDarkMode={isDarkMode} title={title} actions={actions}>
				{children}
			</SectionMessage>
		</BannerWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerWrapper = styled.div({
	maxWidth: '800px',
	minWidth: '500px',
});
